import { DOCUMENT } from '@angular/common';
import {
    AfterViewInit,
    ChangeDetectionStrategy,
    Component,
    ElementRef,
    HostListener,
    Inject,
    Input,
    NgZone,
    OnDestroy,
    OnInit,
    Renderer2,
    ViewChild,
} from '@angular/core';
import { combineLatest, Observable, Subject } from 'rxjs';
import { map, take, takeUntil } from 'rxjs/operators';
import SwiperCore, { EffectFade, Lazy, Navigation } from 'swiper/core';
import { LazyOptions } from 'swiper/types';

import { carImageFallbackIcon, checkmarkIcon, crossLightIcon, pinIcon } from '../../icons';
import { CarDetailsService } from '../car-details-service/car-details.service';
import { getSizedBilinfoImage, roundToClosestNumber } from '../core/helpers';
import { SettingsService, SiteIds } from '../core/settings.service';
import { FilterService } from '../filter-service/filter.service';
import { FeatureDetectionService } from '../utils/helpers/feature-detection.service';
import { ILeasingConfiguratorData, LEASING_PLAN_TYPES, LeasingService } from '../leasing/leasing.service';
import { ICarDetails } from '@impact/data';
import { UsedCarService } from '../used-car-details/used-car.service';
import { Router } from '@angular/router';
import { NavigationService } from '../navigation/navigation.service';

SwiperCore.use([EffectFade, Navigation, Lazy]);

@Component({
    selector: 'app-product-item',
    template: `
        <article class="product-item" #componentRef>
            <div class="product-item__tag" *ngIf="product?.isNew?.data">{{ product?.isNew?.value }}</div>
            <ng-container [ngSwitch]="isB2B$ | async">
                <ng-container *ngSwitchCase="true">
                    <a [routerLink]="[b2bUsedCarsOverviewPageUrl, product.id]"
                        (click)="setCurrentYPos()"
                        appInViewport
                        [observeOutersection]="isOnPLP"
                        (intersectOutput)="intersectInitComponent($event)">
                        <div class="product-item__image image-ratio-wrapper"
                            *ngIf="resizedPictures.length">
                            <swiper
                                *ngIf="showImageSlider && isBrowser"
                                [preloadImages]="false"
                                [lazy]="lazyOptions"
                                [index]="currentImageIndex"
                                (slideChange)="onSlideChange($event)"
                                [pagination]="resizedPictures.length > 1 ? { dynamicBullets: true } : false"
                                [navigation]="showNavigation"
                                [effect]="'fade'">
                                <ng-template swiperSlide *ngFor="let picture of resizedPictures">
                                    <img [attr.data-src]="picture" class="swiper-lazy" [alt]="altText" />
                                </ng-template>
                            </swiper>

                            <img *ngIf="resizedPictures.length; else fallbackIcon"
                                class="product-item__initial-image"
                                [src]="resizedPictures[0]"
                                loading="lazy"
                                [alt]="altText"
                            />

                            <ng-template #fallbackIcon>
                                <div class="product-item__image-fallback">
                                    <i class="product-item__image-fallback-icon">${carImageFallbackIcon}</i>
                                    <p class="product-item__image-fallback-text">{{ 'filterpage.image_fallback_text' | translate }}</p>
                                </div>
                            </ng-template>
                        </div>

                        <div class="product-item__content">
                            <div class="product-item__info">
                                <h3 class="product-item__info-header">
                                    {{ product?.make?.value }} {{ product?.model?.value }}
                                    <div class="product-item__info-header--secondary">{{carModelDataToDecimal}}  {{ product?.variant?.value }}</div>
                                </h3>
                                <div class="product-item__info-text">
                                    <span>{{ product?.year?.value }},  </span>
                                    <span>{{ product?.mileage?.data | number }} km.</span>
                                    <div *ngIf="product?.dealerAlternativeName?.value" class="product-item__info-dealer">
                                        <span class="product-item__info-dealer-icon">${pinIcon}</span>
                                        {{ product?.dealerAlternativeName?.value }}
                                    </div>
                                </div>

                                <div class="product-item__price">
                                    <p class="text-small text-secondary no-margin line-through" *ngIf="filterService.exchangeCarValue | async as excv">
                                    {{ product?.cashPrice?.data | defaultcurrency }}
                                    </p>
                                    <ng-container [ngSwitch]="site">
                                        <!-- MOTORPOINT leasing view -->
                                        <ng-container *ngSwitchCase="siteIds.MOTORPOINT">
                                            <ng-container *ngIf="activeLeasingPlan$ | async as activeLeasingPlan">
                                                <!-- FINANCIAL PRIVATE/BUSINESS -->
                                                <ng-container
                                                    *ngIf="activeLeasingPlan.leasingType === LeasingPlanTypes.TYPE_FINANCIAL && activeLeasingPlan.leasingPurpose === LeasingPlanTypes.PURPOSE_BUSINESS"
                                                    [ngSwitch]="product?.leasingPriceBusinessFinancial?.data < 1000">
                                                    <ng-container *ngSwitchCase="true"><h4>{{'generic.call_for_price' | translate }}</h4></ng-container>
                                                    <ng-container *ngSwitchCase="false">
                                                        <h4>{{product?.leasingPriceBusinessFinancial?.value}}/{{ 'generic.month_abbreviation' | translate }}</h4>
                                                        <h6 *ngIf="product?.leasingPriceBusinessFinancial?.value">{{ 'generic.vat_excluded' | translate }}</h6>
                                                        <p class="product-item__price-loan">{{'filterpage.product_card_car_downpayment_text_prefix' | translate }} {{ product?.leasingDownpaymentBusinessFinancial?.data | defaultcurrency  }}</p>
                                                        <p class="product-item__price-loan">{{'filterpage.product_card_car_monthlyterm_text_prefix' | translate }} {{ monthlyTerms }}  {{ 'generic.months' | translate }}</p>
                                                    </ng-container>
                                                </ng-container>
                                                <ng-container *ngIf="activeLeasingPlan.leasingType === LeasingPlanTypes.TYPE_FINANCIAL && activeLeasingPlan.leasingPurpose === LeasingPlanTypes.PURPOSE_PRIVATE"
                                                    [ngSwitch]="product?.leasingPricePrivateFinancial?.data < 1000">
                                                    <ng-container *ngSwitchCase="true"><h4>{{'generic.call_for_price' | translate }}</h4></ng-container>
                                                    <ng-container *ngSwitchCase="false">
                                                        <h4>{{product?.leasingPricePrivateFinancial?.value}}/{{ 'generic.month_abbreviation' | translate }}</h4>
                                                        <p class="product-item__price-loan">{{'filterpage.product_card_car_downpayment_text_prefix' | translate }} {{ product?.leasingDownpaymentPrivateFinancial?.data | defaultcurrency  }}</p>
                                                        <p class="product-item__price-loan">{{'filterpage.product_card_car_monthlyterm_text_prefix' | translate }} {{ monthlyTerms }}  {{ 'generic.months' | translate }}</p>
                                                    </ng-container>
                                                </ng-container>
                                                <!-- OPERATIONAL PRIVATE/BUSINESS -->
                                                <ng-container *ngIf="activeLeasingPlan.leasingType === LeasingPlanTypes.TYPE_OPERATIONAL && activeLeasingPlan.leasingPurpose === LeasingPlanTypes.PURPOSE_BUSINESS"
                                                    [ngSwitch]="product?.leasingPriceBusinessOperational?.data < 1000">
                                                    <ng-container *ngSwitchCase="true"><h4>{{'generic.call_for_price' | translate }}</h4></ng-container>
                                                    <ng-container *ngSwitchCase="false">
                                                        <h4>{{product?.leasingPriceBusinessOperational?.value}}/{{ 'generic.month_abbreviation' | translate }} </h4>
                                                        <h6 *ngIf="product?.leasingPriceBusinessOperational?.value">{{ 'generic.vat_excluded' | translate }}</h6>
                                                        <p class="product-item__price-loan">{{'filterpage.product_card_car_downpayment_text_prefix' | translate }} {{ product?.leasingDownpaymentBusinessOperational?.data | defaultcurrency  }}</p>
                                                        <p class="product-item__price-loan">{{'filterpage.product_card_car_monthlyterm_text_prefix' | translate }} {{ monthlyTerms }}  {{ 'generic.months' | translate }}</p>
                                                    </ng-container>
                                                </ng-container>
                                                <ng-container *ngIf="activeLeasingPlan.leasingType === LeasingPlanTypes.TYPE_OPERATIONAL && activeLeasingPlan.leasingPurpose === LeasingPlanTypes.PURPOSE_PRIVATE"
                                                    [ngSwitch]="product?.leasingPricePrivateOperational?.data < 1000">
                                                    <ng-container *ngSwitchCase="true"><h4>{{'generic.call_for_price' | translate }}</h4></ng-container>
                                                    <ng-container *ngSwitchCase="false">
                                                        <h4>{{product?.leasingPricePrivateOperational?.value}}/{{ 'generic.month_abbreviation' | translate }}</h4>
                                                        <p class="product-item__price-loan">{{'filterpage.product_card_car_downpayment_text_prefix' | translate }} {{ product?.leasingDownpaymentPrivateOperational?.data | defaultcurrency  }}</p>
                                                        <p class="product-item__price-loan">{{'filterpage.product_card_car_monthlyterm_text_prefix' | translate }} {{ monthlyTerms }}  {{ 'generic.months' | translate }}</p>
                                                    </ng-container>
                                                </ng-container>
                                            </ng-container>
                                        </ng-container>
                                        <ng-container *ngSwitchDefault>
                                            <ng-container *ngIf="showLeasingPrices || isLeasingOnlyPage; else showCashPice">
                                                <h4 class="product-item__price-value">
                                                    {{product?.leasingPricePrivateOperational?.value}}/{{ 'generic.month_abbreviation' | translate }}
                                                </h4>
                                                <p class="product-item__price-loan">{{'filterpage.product_card_car_downpayment_text_prefix' | translate }} {{ leasingDownPaymentValue | defaultcurrency  }}</p>
                                                <p class="product-item__price-loan">{{'filterpage.product_card_car_monthlyterm_text_prefix' | translate }} {{ monthlyTerms }}  {{ 'generic.months' | translate }}</p>
                                            </ng-container>
                                            <ng-template #showCashPice>
                                                <h4  class="product-item__price-value">
                                                    {{
                                                        ((product?.cashPrice?.data || 0) - ((filterService.exchangeCarValue | async) || 0) < 0
                                                            ? 0
                                                            : (product?.cashPrice?.data || 0) - ((filterService.exchangeCarValue | async) || 0)
                                                        ) | defaultcurrency
                                                    }}
                                                </h4>
                                            </ng-template>
                                        </ng-container>
                                    </ng-container>

                                    <p class="product-item__price-disclaimer text-secondary no-margin" *ngIf="filterService.exchangeCarValue | async as excv">
                                        {{ 'filterpage.exchange_car_facet____product_card_disclaimer_text' | translate }}
                                    </p>

                                    <ng-container *ngIf="site !== siteIds.MOTORPOINT" class="product-item__price-loan">
                                        <p>
                                            <span *ngIf="product?.santanderPaymentPerMonth?.data && !showLeasingPrices && !isLeasingOnlyPage ">
                                                {{ 'filterpage.product_card_car_loan_text_prefix' | translate }}
                                                {{ product?.santanderPaymentPerMonth?.data | defaultcurrency }}/{{ 'generic.month_abbreviation' | translate }}
                                            </span>
                                        </p>
                                    </ng-container>
                                </div>
                            </div>

                            <div *ngIf="site !== siteIds.MOTORPOINT && !showLeasingPrices && !isLeasingOnlyPage" class="product-item__valuation">
                                <app-product-item-valuation-badge [valuationString]="product?.indicataPriceLevel?.value"></app-product-item-valuation-badge>
                            </div>

                            <ng-container [ngSwitch]="site">
                                <ng-container *ngSwitchCase="siteIds.MOTORPOINT">
                                <div class="product-item__buying-options">
                                        <div class="product-item__buying-option" [ngSwitch]="product?.isBuyable?.data">
                                            <ng-container *ngSwitchCase="true">
                                                <span class="checkmark-icon">${checkmarkIcon}</span> {{ 'filterpage.buy_online' | translate }}
                                            </ng-container>
                                            <ng-container *ngSwitchCase="false">
                                                <span class="cross-icon">${crossLightIcon}</span> {{ 'filterpage.buy_online' | translate }}
                                            </ng-container>
                                        </div>
                                        <div class="product-item__buying-option">
                                            <span class="checkmark-icon">${checkmarkIcon}</span> {{ 'filterpage.buy_in_showroom' | translate }}
                                        </div>
                                    </div>
                                </ng-container>
                                <ng-container *ngSwitchDefault>
                                    <div class="product-item__buying-options">
                                        <div class="product-item__buying-option" [ngSwitch]="product?.isBuyable?.data">
                                            <ng-container *ngSwitchCase="true">
                                                <span class="checkmark-icon">${checkmarkIcon}</span> {{ 'filterpage.product_card_buy_online_text' | translate }}
                                            </ng-container>
                                            <ng-container *ngSwitchCase="false">
                                                <span class="cross-icon">${crossLightIcon}</span> {{ 'filterpage.product_card_buy_online_text' | translate }}
                                            </ng-container>
                                        </div>
                                        <div class="product-item__buying-option">
                                            <span class="checkmark-icon">${checkmarkIcon}</span> {{ 'filterpage.product_card_buy_at_dealership_text' | translate }}
                                        </div>
                                    </div>
                                </ng-container>
                            </ng-container>
                        </div>
                    </a>
                </ng-container>
                <ng-container *ngSwitchCase="false">
                    <a [routerLink]="[usedCarsOverviewPageUrl, product.id]"
                    (click)="setCurrentYPos()"
                    appInViewport
                    [observeOutersection]="isOnPLP"
                    (intersectOutput)="intersectInitComponent($event)">
                    <div class="product-item__image image-ratio-wrapper"
                        *ngIf="resizedPictures.length">
                        <swiper
                            *ngIf="showImageSlider && isBrowser"
                            [preloadImages]="false"
                            [lazy]="lazyOptions"
                            [index]="currentImageIndex"
                            (slideChange)="onSlideChange($event)"
                            [pagination]="resizedPictures.length > 1 ? { dynamicBullets: true } : false"
                            [navigation]="showNavigation"
                            [effect]="'fade'">
                            <ng-template swiperSlide *ngFor="let picture of resizedPictures">
                                <img [attr.data-src]="picture" class="swiper-lazy" [alt]="altText" />
                            </ng-template>
                        </swiper>

                        <img *ngIf="resizedPictures.length; else fallbackIcon"
                            class="product-item__initial-image"
                            [src]="resizedPictures[0]"
                            loading="lazy"
                            [alt]="altText"
                        />

                        <ng-template #fallbackIcon>
                            <div class="product-item__image-fallback">
                                <i class="product-item__image-fallback-icon">${carImageFallbackIcon}</i>
                                <p class="product-item__image-fallback-text">{{ 'filterpage.image_fallback_text' | translate }}</p>
                            </div>
                        </ng-template>
                    </div>

                    <div class="product-item__content">
                        <div class="product-item__info">
                            <h3 class="product-item__info-header">
                                {{ product?.make?.value }} {{ product?.model?.value }}
                                <div class="product-item__info-header--secondary">{{carModelDataToDecimal}}  {{ product?.variant?.value }}</div>
                            </h3>
                            <div class="product-item__info-text">
                                <span>{{ product?.year?.value }},  </span>
                                <span>{{ product?.mileage?.data | number }} km.</span>
                                <div *ngIf="product?.dealerAlternativeName?.value" class="product-item__info-dealer">
                                    <span class="product-item__info-dealer-icon">${pinIcon}</span>
                                    {{ product?.dealerAlternativeName?.value }}
                                </div>
                            </div>

                            <div class="product-item__price">
                                <p class="text-small text-secondary no-margin line-through" *ngIf="filterService.exchangeCarValue | async as excv">
                                {{ product?.cashPrice?.data | defaultcurrency }}
                                </p>

                                <ng-container [ngSwitch]="site">
                                    <!-- MOTORPOINT leasing view -->
                                    <ng-container *ngSwitchCase="siteIds.MOTORPOINT">
                                        <ng-container *ngIf="activeLeasingPlan$ | async as activeLeasingPlan">
                                            <!-- FINANCIAL PRIVATE/BUSINESS -->
                                            <ng-container
                                                *ngIf="activeLeasingPlan.leasingType === LeasingPlanTypes.TYPE_FINANCIAL && activeLeasingPlan.leasingPurpose === LeasingPlanTypes.PURPOSE_BUSINESS"
                                                [ngSwitch]="product?.leasingPriceBusinessFinancial?.data < 1000">
                                                <ng-container *ngSwitchCase="true">
                                                    <div class="animate-fade-in">
                                                        <h4 *ngIf="showCashPrice">{{ product?.cashPrice?.data | defaultcurrency }}</h4>
                                                        <h4 *ngIf="!showCashPrice">{{ 'generic.call_for_price' | translate }}</h4>
                                                    </div>
                                                </ng-container>
                                                <ng-container *ngSwitchCase="false">
                                                    <h4>{{product?.leasingPriceBusinessFinancial?.value}}/{{ 'generic.month_abbreviation' | translate }}</h4>
                                                    <h6 *ngIf="product?.leasingPriceBusinessFinancial?.value">{{ 'generic.vat_excluded' | translate }}</h6>
                                                    <p class="product-item__price-loan">{{'filterpage.product_card_car_downpayment_text_prefix' | translate }} {{ product?.leasingDownpaymentBusinessFinancial?.data | defaultcurrency  }}</p>
                                                    <p class="product-item__price-loan">{{'filterpage.product_card_car_monthlyterm_text_prefix' | translate }} {{ monthlyTerms }}  {{ 'generic.months' | translate }}</p>
                                                </ng-container>
                                            </ng-container>
                                            <ng-container *ngIf="activeLeasingPlan.leasingType === LeasingPlanTypes.TYPE_FINANCIAL && activeLeasingPlan.leasingPurpose === LeasingPlanTypes.PURPOSE_PRIVATE"
                                                [ngSwitch]="product?.leasingPricePrivateFinancial?.data < 1000">
                                                <ng-container *ngSwitchCase="true">
                                                    <div class="animate-fade-in">
                                                        <h4 *ngIf="showCashPrice">{{ product?.cashPrice?.data | defaultcurrency }}</h4>
                                                        <h4 *ngIf="!showCashPrice">{{ 'generic.call_for_price' | translate }}</h4>
                                                    </div>
                                                </ng-container>
                                                <ng-container *ngSwitchCase="false">
                                                    <h4>{{product?.leasingPricePrivateFinancial?.value}}/{{ 'generic.month_abbreviation' | translate }}</h4>
                                                    <p class="product-item__price-loan">{{'filterpage.product_card_car_downpayment_text_prefix' | translate }} {{ product?.leasingDownpaymentPrivateFinancial?.data | defaultcurrency  }}</p>
                                                    <p class="product-item__price-loan">{{'filterpage.product_card_car_monthlyterm_text_prefix' | translate }} {{ monthlyTerms }}  {{ 'generic.months' | translate }}</p>
                                                </ng-container>
                                            </ng-container>
                                            <!-- OPERATIONAL PRIVATE/BUSINESS -->
                                            <ng-container *ngIf="activeLeasingPlan.leasingType === LeasingPlanTypes.TYPE_OPERATIONAL && activeLeasingPlan.leasingPurpose === LeasingPlanTypes.PURPOSE_BUSINESS"
                                                [ngSwitch]="product?.leasingPriceBusinessOperational?.data < 1000">
                                                <ng-container *ngSwitchCase="true">
                                                    <div class="animate-fade-in">
                                                        <h4 *ngIf="showCashPrice">{{ product?.cashPrice?.data | defaultcurrency }}</h4>
                                                        <h4 *ngIf="!showCashPrice">{{ 'generic.call_for_price' | translate }}</h4>
                                                    </div>
                                                </ng-container>
                                                <ng-container *ngSwitchCase="false">
                                                    <h4>{{product?.leasingPriceBusinessOperational?.value}}/{{ 'generic.month_abbreviation' | translate }} </h4>
                                                    <h6 *ngIf="product?.leasingPriceBusinessOperational?.value">{{ 'generic.vat_excluded' | translate }}</h6>
                                                    <p class="product-item__price-loan">{{'filterpage.product_card_car_downpayment_text_prefix' | translate }} {{ product?.leasingDownpaymentBusinessOperational?.data | defaultcurrency  }}</p>
                                                    <p class="product-item__price-loan">{{'filterpage.product_card_car_monthlyterm_text_prefix' | translate }} {{ monthlyTerms }}  {{ 'generic.months' | translate }}</p>
                                                </ng-container>
                                            </ng-container>
                                            <ng-container *ngIf="activeLeasingPlan.leasingType === LeasingPlanTypes.TYPE_OPERATIONAL && activeLeasingPlan.leasingPurpose === LeasingPlanTypes.PURPOSE_PRIVATE"
                                                [ngSwitch]="product?.leasingPricePrivateOperational?.data < 1000">
                                                <ng-container *ngSwitchCase="true">
                                                    <div class="animate-fade-in">
                                                        <h4 *ngIf="showCashPrice">{{ product?.cashPrice?.data | defaultcurrency }}</h4>
                                                        <h4 *ngIf="!showCashPrice">{{ 'generic.call_for_price' | translate }}</h4>
                                                    </div>
                                                </ng-container>
                                                <ng-container *ngSwitchCase="false">
                                                    <h4>{{product?.leasingPricePrivateOperational?.value}}/{{ 'generic.month_abbreviation' | translate }}</h4>
                                                    <p class="product-item__price-loan">{{'filterpage.product_card_car_downpayment_text_prefix' | translate }} {{ product?.leasingDownpaymentPrivateOperational?.data | defaultcurrency  }}</p>
                                                    <p class="product-item__price-loan">{{'filterpage.product_card_car_monthlyterm_text_prefix' | translate }} {{ monthlyTerms }}  {{ 'generic.months' | translate }}</p>
                                                </ng-container>
                                            </ng-container>
                                        </ng-container>
                                    </ng-container>
                                    <ng-container *ngSwitchDefault>
                                        <ng-container *ngIf="showLeasingPrices || isLeasingOnlyPage; else showCashPice">
                                            <h4 class="product-item__price-value">
                                                {{product?.leasingPricePrivateOperational?.value}}/{{ 'generic.month_abbreviation' | translate }}
                                            </h4>
                                            <p class="product-item__price-loan">{{'filterpage.product_card_car_downpayment_text_prefix' | translate }} {{ leasingDownPaymentValue | defaultcurrency  }}</p>
                                            <p class="product-item__price-loan">{{'filterpage.product_card_car_monthlyterm_text_prefix' | translate }} {{ monthlyTerms }}  {{ 'generic.months' | translate }}</p>
                                        </ng-container>
                                        <ng-template #showCashPice>
                                            <h4  class="product-item__price-value">
                                                {{
                                                    ((product?.cashPrice?.data || 0) - ((filterService.exchangeCarValue | async) || 0) < 0
                                                        ? 0
                                                        : (product?.cashPrice?.data || 0) - ((filterService.exchangeCarValue | async) || 0)
                                                    ) | defaultcurrency
                                                }}
                                            </h4>
                                        </ng-template>
                                    </ng-container>
                                </ng-container>

                                <p class="product-item__price-disclaimer text-secondary no-margin" *ngIf="filterService.exchangeCarValue | async as excv">
                                    {{ 'filterpage.exchange_car_facet____product_card_disclaimer_text' | translate }}
                                </p>

                                <ng-container *ngIf="site !== siteIds.MOTORPOINT" class="product-item__price-loan">
                                    <p>
                                        <span *ngIf="product?.santanderPaymentPerMonth?.data && !showLeasingPrices && !isLeasingOnlyPage ">
                                            {{ 'filterpage.product_card_car_loan_text_prefix' | translate }}
                                            {{ product?.santanderPaymentPerMonth?.data | defaultcurrency }}/{{ 'generic.month_abbreviation' | translate }}
                                        </span>
                                    </p>
                                </ng-container>
                            </div>
                        </div>

                        <div *ngIf="site !== siteIds.MOTORPOINT && !showLeasingPrices && !isLeasingOnlyPage" class="product-item__valuation">
                            <app-product-item-valuation-badge [valuationString]="product?.indicataPriceLevel?.value"></app-product-item-valuation-badge>
                        </div>

                        <ng-container [ngSwitch]="site">
                            <ng-container *ngSwitchCase="siteIds.MOTORPOINT">
                            <div class="product-item__buying-options">
                                    <div class="product-item__buying-option" [ngSwitch]="product?.isBuyable?.data">
                                        <ng-container *ngSwitchCase="true">
                                            <span class="checkmark-icon">${checkmarkIcon}</span> {{ 'filterpage.buy_online' | translate }}
                                        </ng-container>
                                        <ng-container *ngSwitchCase="false">
                                            <span class="cross-icon">${crossLightIcon}</span> {{ 'filterpage.buy_online' | translate }}
                                        </ng-container>
                                    </div>
                                    <div class="product-item__buying-option">
                                        <span class="checkmark-icon">${checkmarkIcon}</span> {{ 'filterpage.buy_in_showroom' | translate }}
                                    </div>
                                </div>
                            </ng-container>
                            <ng-container *ngSwitchDefault>
                                <div class="product-item__buying-options">
                                    <div class="product-item__buying-option" [ngSwitch]="product?.isBuyable?.data">
                                        <ng-container *ngSwitchCase="true">
                                            <span class="checkmark-icon">${checkmarkIcon}</span> {{ 'filterpage.product_card_buy_online_text' | translate }}
                                        </ng-container>
                                        <ng-container *ngSwitchCase="false">
                                            <span class="cross-icon">${crossLightIcon}</span> {{ 'filterpage.product_card_buy_online_text' | translate }}
                                        </ng-container>
                                    </div>
                                    <div class="product-item__buying-option">
                                        <span class="checkmark-icon">${checkmarkIcon}</span> {{ 'filterpage.product_card_buy_at_dealership_text' | translate }}
                                    </div>
                                </div>
                            </ng-container>
                        </ng-container>
                    </div>
                    </a>
                </ng-container>
            </ng-container>


        </article>
    `,
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProductItemComponent implements OnInit, AfterViewInit, OnDestroy {
    @ViewChild('componentRef') componentRef: ElementRef;

    @Input() sliderImageSize = 400;
    @Input() sliderImagesAmount?: number;
    @Input() showLeasingPrices: boolean | undefined | null;

    @Input() enableScrollTo? = false;
    @Input() product: any;
    @Input() isOnPLP = false; // If present on PLP it will also unload swiper when not in viewport
    @Input() isLeasingOnlyPage: boolean;
    @Input() showCashPrice: boolean = false;


    resizedPictures: string[];
    isBrowser: boolean;
    altText: string;
    usedCarsOverviewPageUrl = '';
    b2bUsedCarsOverviewPageUrl: string | undefined;
    showImageSlider: boolean;
    isB2B$ = this.navigationService.isB2B$;

    site: string;
    siteIds = SiteIds;

    activeLeasingPlan$: Observable<any>;
    LeasingPlanTypes = LEASING_PLAN_TYPES;
    car$: Observable<ICarDetails>;
    carData: ICarDetails;
    leasingConfiguratorData: ILeasingConfiguratorData;

    fullPrice: number;
    percentOfFullPrice: number;
    leasingDownPaymentValue:number;
	carModelDataToDecimal: number | string;
    monthlyTerms:number;

    currentImageIndex: number;
    showNavigation = false;
    lazyOptions: LazyOptions = {
        loadOnTransitionStart: true,
        checkInView: true,
        loadPrevNext: false
    };

    private unlistenClicks: (() => void)[] = [];
    private readonly unsubscribe = new Subject<void>();

    @HostListener('mouseenter') onMouseEnter() {
        if (this.resizedPictures?.length === 1) {
            return;
        }

        if (!this.showNavigation) {
            this.showNavigation = true;
        }

        const timeout = setTimeout(() => {
            const buttons = this.componentRef.nativeElement.querySelectorAll('.swiper-button-next, .swiper-button-prev');
            this.unlistenToClicks();

            // Stop routerLink navigation
            buttons.forEach((button: any) => this.unlistenClicks.push(this.renderer.listen(button, 'click', (event) => event.stopPropagation())));
            clearTimeout(timeout);
        });
    }

    @HostListener('mouseleave') onMouseLeave() {
        this.unlistenToClicks();
        this.showNavigation = false;
    }

    constructor(
        public filterService: FilterService,
        private router: Router,
        featureDetectionService: FeatureDetectionService,
        private zone: NgZone,
        private renderer: Renderer2,
        private settingsService: SettingsService,
        public navigationService: NavigationService,
        private leasingService: LeasingService,
        private usedCarService: UsedCarService,
        private readonly carDetailsService: CarDetailsService,
        @Inject(DOCUMENT) private document: Document
    ) {
        this.isBrowser = featureDetectionService.isBrowser();

        this.car$ = this.usedCarService.carDetails$;

        this.activeLeasingPlan$ = this.leasingService.getActiveLeasingToggles();

        this.settingsService
            .getGlobalPages()
            .pipe(takeUntil(this.unsubscribe))
            .subscribe((globalPages) => {
                if (globalPages && globalPages.usedCarsOverviewPage) {
                    this.usedCarsOverviewPageUrl = globalPages.usedCarsOverviewPage.url;
                }
            });
        // B2B overviewpage url
        this.settingsService
            .getGlobalPages()
            .pipe(takeUntil(this.unsubscribe))
            .subscribe((globalPages) => {
                if (globalPages && globalPages.b2bUsedCarsOverviewPage ) {
                    //Assigning B2B url
                    this.b2bUsedCarsOverviewPageUrl = globalPages.b2bUsedCarsOverviewPage?.url ?? undefined;
                }
            });
        this.settingsService
        .get()
        .pipe(takeUntil(this.unsubscribe))
        .subscribe((settings) => {
            this.site = settings.siteNameId;
        });

        const carDetailsData$ = this.usedCarService.carDetails$;
        const connectStream$ = combineLatest([carDetailsData$]);
        connectStream$.pipe(
            map(([carDetailsData]) => {
                this.carData = carDetailsData;
            }), takeUntil(this.unsubscribe)).subscribe();

    }

    ngOnInit() {
        this.resizedPictures = this.product.pictures ? this.product.pictures.map((p: any) => getSizedBilinfoImage(p, this.sliderImageSize)) : [];
        this.currentImageIndex = 0;
        this.altText = `${this.product?.make?.value} ${this.product?.model?.value} ${this.product?.variant?.value}`;

        if (this.sliderImagesAmount) {
            this.resizedPictures = this.resizedPictures.slice(0, this.sliderImagesAmount);
        }

        this.fullPrice =this.product?.cashPrice?.data;
        this.percentOfFullPrice = this.product?.leasingDefaultDownPayment?.data;
        this.leasingDownPaymentValue = this.getDefaultDownPayment(this.fullPrice,this.percentOfFullPrice);


        //Leasing Monthly Term (Løbetid)
        this.monthlyTerms = this.product?.leasingDefaultDurationInMonths?.data;

        //Car variant if less than 5 add decimal .0
        let motorData = this.product?.motor?.data;

        if (motorData === 0) {
            motorData = '';
        } else if (motorData <= 5) {
            motorData = motorData.toFixed(1);
        }
        this.carModelDataToDecimal = motorData;
    }

    ngAfterViewInit() {
        this.checkForScrollToComponent();
    }

    getUsedCarPricePerMonth(): Observable<any> {
        return this.leasingService.getUsedCarLeasingPrice(this.carData, this.leasingConfiguratorData);
    }

    intersectInitComponent(intersection: any) {
        if (intersection.isIntersecting) {
            this.showImageSlider = true;
        } else {
            this.showImageSlider = false;
        }
    }

    onSlideChange(swiper: any) {
        this.zone.run(() => {
            this.currentImageIndex = swiper.realIndex;
        });
    }

    unlistenToClicks() {
        if (this.unlistenClicks.length > 0) {
            this.unlistenClicks.forEach((fn: () => void) => fn());
            this.unlistenClicks.length = 0;
        }
    }

    ngOnDestroy() {
        this.unsubscribe.next();
        this.unsubscribe.complete();
        this.unlistenToClicks();
    }

    setCurrentYPos() {
        this.carDetailsService.setVisitedFilterPageYPos(window.scrollY);
    }
    //Default Downpayment Calculation
    getDefaultDownPayment(fullPrice: number, minDownPaymentPct: number): number {
        const percentOfFullPrice = this.product?.leasingDefaultDownPayment?.data;
        if (minDownPaymentPct === 0) {
            minDownPaymentPct = 25;
        }
        const downPayment = roundToClosestNumber(fullPrice * (percentOfFullPrice / 100), 1000);
        return downPayment;

    }

    private checkForScrollToComponent() {
        // Angular is configured to scroll to last known Y position on the browser "back" event.
        // But Angular being Angular, we ensure that the last known Y scroll position is reached
        if (this.enableScrollTo) {
            const carDetailsId$ = this.carDetailsService.getVisitedCarId();
            const carDetailsYPos$ = this.carDetailsService.getVisitedFilterPageYPos();

            const connectStream$ = combineLatest([carDetailsId$, carDetailsYPos$]);
            connectStream$
                .pipe(
                    map(([id, yPos]) => {
                        if (this.product.id && this.product.id === id) {
                            setTimeout(() => {
                                if (this.document) {
                                    this.document.documentElement.scrollTop = yPos;
                                }

                                this.carDetailsService.setVisitedCarId(undefined); // Reset to avoid scrolling more than once
                                this.carDetailsService.setVisitedFilterPageYPos(0);
                            }, 20);
                        }
                    }),
                    take(1)
                ).subscribe();
        }
    }
}
